<script setup>
import { computed, nextTick, onMounted, ref, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import { menuRoutes, menuRouteEnum } from '@/router'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { ABSENCE_COLOR, ABSENCE_ICON, eventTypeEnum } from '@/stores/schedule.store'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'
import { useTimelinePan } from '@/composables/timeline-pan'
import { CENTER_VIEWPORT_RATIO, DAY_LINES_MIN_WIDTH, useTimelineViewport } from '@/composables/timeline-viewport'
import { useHighlightNewRow } from '@/composables/scroll'
import { useDetectDevice } from '@/composables/device'
import { useWarehouseAvailabilityData } from '@/composables/warehouse-availability'
import { includesQuery } from '@/composables/project-timeline'
import ProjectTimelineScale from '@/components/planner/project-timeline/ProjectTimelineScale.vue'
import ListItem from '@/components/shared/ListItem.vue'
import SearchInput from '@/components/shared/SearchInput.vue'
import WarehouseMovementEdit from '@/components/shared/WarehouseMovementEdit.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const data = useWarehouseAvailabilityData()

const { STAFF, EQUIPMENT } = portalStore.serviceTypeEnum
const { OK, BELOW_MIN, SHORTAGE } = planningStore.warehouseAvailabilityStatusEnum
const { LOAD, TRANSFER } = planningStore.warehouseMovementTypeEnum
const { CONFIRMED } = planningStore.warehouseMovementStatusEnum
const { ABSENCE } = eventTypeEnum

const DEFAULT_ZOOM_INDEX = 3
const MAX_ZOOM_INDEX = 5
const INITIAL_SPAN_DAYS = 60
const TASK_LIST_BOUNDS = Object.freeze({ min: 16, max: 40 })
const LABEL_MIN_WIDTH = 2
const LANE_HEIGHT = 1.5
const DRAG_THRESHOLD = 3
const CHART_TOP = 10
const CHART_ZERO = 74
const CHART_BOTTOM = 98
const resourceFilterEnum = Object.freeze({ ALL: 0, EQUIPMENT: 1, STAFF: 2 })
const SERVICE_TYPE_ICONS = Object.freeze({ [STAFF]: 'mdi-account', [EQUIPMENT]: 'mdi-camcorder' })

const warningColor = signalToneColor(signalToneEnum.ATTENTION)
const canEdit = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.MASTER_DATA))

const containerRef = ref(null)
const scrollRef = ref(null)
const taskListWidth = toRef(webSiteStore, 'warehouseAvailabilityTaskListWidth')
const onlyCritical = ref(false)
const resourceSearch = ref('')
const resourceFilter = ref(resourceFilterEnum.ALL)
const warehouseId = ref(null)
const expanded = ref(new Set())
const hover = ref(null)
const actionMenu = ref(null)
const movementEdit = ref(null)
const minStockItemId = ref(null)

const {
  zoomIndex,
  rangeStart,
  rangeDays,
  dayWidth,
  trackWidth,
  canZoomIn,
  canZoomOut,
  dayPixels,
  activePreset,
  presetOptions,
  initRange,
  scrollToDate,
  scrollToToday,
  extendRangeIfNeededAsync,
  setZoomAsync,
  onWheel,
  resizeTaskList,
  gridStyle,
  todayLeft,
} = useTimelineViewport({
  scrollRef,
  taskListWidth,
  zoomIndex: ref(DEFAULT_ZOOM_INDEX),
  maxZoomIndex: MAX_ZOOM_INDEX,
  taskListBounds: TASK_LIST_BOUNDS,
  onExtend: () => loadWindowAsync(),
})

const loadWindowAsync = () => data.loadAsync(rangeStart.value, rangeDays.value)

// Days of the loaded data from the viewport start: non-zero only while a grown window is loading.
const dataOffset = computed(() => data.state.rangeStart.diff(rangeStart.value, 'day'))
const seriesStyle = computed(() => ({
  left: `${dataOffset.value * dayWidth.value}rem`,
  width: `${data.state.rangeDays * dayWidth.value}rem`,
}))

const resourceOptions = computed(() => [
  { value: resourceFilterEnum.ALL, label: t('warehouseAvailability.all') },
  { value: resourceFilterEnum.EQUIPMENT, label: t('common.equipment', 2), icon: SERVICE_TYPE_ICONS[EQUIPMENT] },
  { value: resourceFilterEnum.STAFF, label: t('common.staff', 2), icon: SERVICE_TYPE_ICONS[STAFF] },
])
const warehouseOptions = computed(() => [
  { value: null, label: t('warehouseAvailability.allWarehouses') },
  ...data.state.warehouses.map(w => ({ value: w.id, label: w.name })),
])

const dateFormatter = computed(() => new Intl.DateTimeFormat(locale.value, { weekday: 'short', day: 'numeric', month: 'short' }))
const shortDateFormatter = computed(() => new Intl.DateTimeFormat(locale.value, { day: 'numeric', month: 'short' }))
const formatDay = (value) => dateFormatter.value.format(dayjs(value).toDate())
const formatShortDay = (value) => shortDateFormatter.value.format(dayjs(value).toDate())
const formatPeriod = ({ start, end }) => (dayjs(start).isSame(end, 'day')
  ? formatShortDay(start)
  : t('warehouseAvailability.period', { from: formatShortDay(start), to: formatShortDay(end) }))

// --- Data ---
const items = computed(() => data.build(warehouseId.value))

const supportsMinStock = (item) => !item.isStaff && !item.is_serialized

const stockToday = (item, id) => {
  const today = item.days[Math.min(Math.max(data.dayIndex(dayjs()), 0), item.days.length - 1)]
  return today?.byWarehouse.find(w => w.id === id)?.physical ?? 0
}

const onMinStockChange = (item, id, value) => {
  const quantity = value === '' || value === null ? null : Math.max(0, Number(value))
  data.setMinStockAsync(item.id, id, quantity)
}

const isCritical = (item) => item.shortages.length > 0 || item.belowMin.length > 0
const firstCriticalIndex = (item) => Math.min(...[...item.shortages, ...item.belowMin].map(e => e.startIndex))

const visibleItems = computed(() => items.value
  .filter(item => resourceFilter.value === resourceFilterEnum.ALL ||
    (resourceFilter.value === resourceFilterEnum.STAFF) === item.isStaff)
  .filter(item => !onlyCritical.value || isCritical(item))
  .filter(item => !resourceSearch.value || includesQuery(itemTitle(item), resourceSearch.value.toLowerCase().trim()))
  .sort((a, b) => {
    const critical = Number(isCritical(b)) - Number(isCritical(a))
    if (critical) return critical
    if (isCritical(a)) return firstCriticalIndex(a) - firstCriticalIndex(b)
    return a.type - b.type || a.name.localeCompare(b.name)
  }))

const criticalEntries = computed(() => visibleItems.value
  .flatMap(item => [...item.shortages, ...item.belowMin].map(episode => ({ item, episode, key: `${item.id}-${episode.status}-${episode.startIndex}` })))
  .sort((a, b) => a.episode.startIndex - b.episode.startIndex))
const shortageCount = computed(() => criticalEntries.value.filter(e => e.episode.status === SHORTAGE).length)
const belowMinCount = computed(() => criticalEntries.value.length - shortageCount.value)
const criticalLabel = computed(() => [
  `${shortageCount.value} ${t('warehouseAvailability.shortages')}`,
  belowMinCount.value ? t('warehouseAvailability.belowMinCount', { count: belowMinCount.value }) : null,
].filter(Boolean).join(' · '))

const itemTitle = (item) => planningStore.getServiceName(item)
const itemSubtitle = (item) => (item.isStaff ? t('common.staff', 1) : `${t('warehouseAvailability.owned')} ${item.owned}`)
const minStockLabel = (item) => (item.minStock === null
  ? t('warehouseAvailability.setMinStock')
  : `${t('warehouseAvailability.minStock')} ${item.minStock}`)

const episodeLabel = (episode) => {
  if (episode.status === BELOW_MIN) return t('warehouseAvailability.belowMin')
  return episode.real ? t('warehouseAvailability.realShortage') : t('warehouseAvailability.potentialShortage')
}

// --- Navigation ---
const dayLeft = (index) => index * dayWidth.value

// --- Pan and click on the track ---
const pan = useTimelinePan(containerRef)
let pointerDown = null

const onPointerMove = (event) => pan.move(event)

const onPointerUp = (event) => {
  window.removeEventListener('pointermove', onPointerMove)
  pan.end()
  const down = pointerDown
  pointerDown = null
  extendRangeIfNeededAsync()
  if (!down?.onClick) return
  if (Math.hypot(event.clientX - down.x, event.clientY - down.y) < DRAG_THRESHOLD) down.onClick(event, down.target)
}

const onPanStart = (event, onClick = null) => {
  if (event.button !== 0) return
  minStockItemId.value = null
  pointerDown = { x: event.clientX, y: event.clientY, target: event.currentTarget, onClick }
  pan.beginElement(event, scrollRef.value, scrollRef.value)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp, { once: true })
}

const onScroll = () => {
  if (pan.isPanning()) return
  extendRangeIfNeededAsync()
}

const onResizerPointerDown = (event) => {
  minStockItemId.value = null
  event.preventDefault()
  event.stopPropagation()
  const origin = { originX: event.clientX, width: taskListWidth.value }
  const onMove = (e) => resizeTaskList(origin, e.clientX)
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', () => window.removeEventListener('pointermove', onMove), { once: true })
}

// --- Chart ---
const chartY = (item, quantity) => {
  const unit = (CHART_ZERO - CHART_TOP) / item.scale
  return Math.min(CHART_BOTTOM, Math.max(2, CHART_ZERO - quantity * unit))
}

const stepPath = (item, valueOf) => item.days
  .map((day, i) => `${i === 0 ? 'M' : 'L'}${i},${chartY(item, valueOf(day))} L${i + 1},${chartY(item, valueOf(day))}`)
  .join(' ')

const areaPath = (item) => `M0,${CHART_ZERO} ${stepPath(item, d => Math.max(d.physical, 0)).replace(/^M/, 'L')} L${item.days.length},${CHART_ZERO} Z`
const projectedPath = (item) => stepPath(item, d => d.projected)

const deficitRects = (item) => item.days
  .filter(day => day.status === SHORTAGE)
  .map(day => ({ x: day.index, y: CHART_ZERO, height: chartY(item, day.projected) - CHART_ZERO, real: day.physical < 0 }))

const bandStyle = (episode) => ({
  left: `${dayLeft(episode.startIndex)}rem`,
  width: `${(episode.endIndex - episode.startIndex + 1) * dayWidth.value}rem`,
})

const episodeBadge = (episode) => (episode.status === SHORTAGE ? `${episode.worst}` : '!')

// --- Hover tooltip ---
const dayAtEvent = (event, track, item) => {
  const index = Math.floor((event.clientX - track.getBoundingClientRect().left) / dayPixels()) - dataOffset.value
  return item.days[Math.min(Math.max(index, 0), item.days.length - 1)]
}

const onTrackMove = (event, item) => {
  if (pan.isPanning()) {
    hover.value = null
    return
  }
  hover.value = { item, day: dayAtEvent(event, event.currentTarget, item), x: event.clientX, y: event.clientY }
}

const hoverStatusLabel = computed(() => {
  const day = hover.value?.day
  if (!day || day.status === OK) return null
  if (day.status === BELOW_MIN) return t('warehouseAvailability.belowMin')
  return day.physical < 0 ? t('warehouseAvailability.realShortage') : t('warehouseAvailability.potentialShortage')
})

// --- Expanded lanes ---
const toggleExpanded = (item) => {
  const next = new Set(expanded.value)
  if (next.has(item.id)) next.delete(item.id)
  else next.add(item.id)
  expanded.value = next
}

const commitmentBars = (item) => {
  const laneEnds = []
  return item.commitments.map((bar) => {
    let lane = laneEnds.findIndex(end => end < bar.startIndex)
    if (lane === -1) lane = laneEnds.push(bar.endIndex) - 1
    else laneEnds[lane] = bar.endIndex
    return { ...bar, lane, key: `${bar.id}-${bar.startIndex}` }
  })
}

const laneHeight = (item) => `${Math.max(1, ...commitmentBars(item).map(bar => bar.lane + 1)) * LANE_HEIGHT + 0.5}rem`

const barColor = (bar) => (bar.type === ABSENCE ? ABSENCE_COLOR : bar.project?.color ?? 'var(--q-primary)')
const barLabel = (bar) => (bar.type === ABSENCE ? t('common.staff.absence') : `${bar.project?.name ?? ''} · ×${bar.quantity}`)
const commitmentLine = (bar) => [
  barLabel(bar),
  bar.type === ABSENCE ? null : (bar.confirmed ? t('common.confirmed') : t('common.notConfirmed')),
].filter(Boolean).join(' · ')
const barTooltip = (bar) => `${commitmentLine(bar)} · ${formatPeriod(bar)}`

const isIncoming = (movement) => movement.movement_type === LOAD

const movementIcon = (movement) => {
  if (movement.from_virtual) return 'mdi-truck-delivery-outline'
  return isIncoming(movement) ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold'
}
const movementLabel = (movement) => {
  if (movement.is_transfer && movement.other_warehouse_name) {
    return isIncoming(movement)
      ? t('warehouseAvailability.arrivalFrom', { warehouse: movement.other_warehouse_name })
      : t('warehouseAvailability.transferTo', { warehouse: movement.other_warehouse_name })
  }
  return isIncoming(movement) ? t('warehouseAvailability.incoming') : t('warehouseAvailability.outgoing')
}
const movementStatusLabel = (movement) => {
  if (movement.planned) return t('warehouseAvailability.plannedMovement')
  return movement.status === CONFIRMED ? t('warehouseAvailability.confirmedMovement') : t('warehouseAvailability.scheduledMovement')
}
const movementLine = (movement) => [
  `${movementLabel(movement)} ×${movement.quantity}`,
  movement.project?.name,
  movementStatusLabel(movement),
].filter(Boolean).join(' · ')

// One marker per day, direction, origin and kind (real/planned), with the summed quantity.
const movementMarkers = (item) => {
  const groups = new Map()
  for (const movement of item.movements) {
    const index = data.dayIndex(movement.scheduled_at)
    const key = [index, isIncoming(movement), movement.from_virtual, movement.planned].join('-')
    const group = groups.get(key) ?? { key, index, movements: [], quantity: 0, sample: movement }
    group.movements.push(movement)
    group.quantity += movement.quantity
    groups.set(key, group)
  }
  const slots = new Map()
  return [...groups.values()].map((group) => {
    const slotKey = `${group.index}-${isIncoming(group.sample)}`
    const slot = slots.get(slotKey) ?? 0
    slots.set(slotKey, slot + 1)
    return { ...group, slot }
  })
}

const markerStyle = (marker) => ({
  left: `${(marker.index + 0.5) * dayWidth.value}rem`,
  '--marker-slot': marker.slot,
})

const dayCommitments = (item, day) => item.commitments.filter(bar => bar.startIndex <= day.index && bar.endIndex >= day.index)
const dayMovements = (item, day) => item.movements.filter(movement => data.dayIndex(movement.scheduled_at) === day.index)

// --- Actions ---
const openInTimeline = (projectId, serviceId, date) => router.push({
  path: menuRoutes[menuRouteEnum.TIMELINE],
  query: { projectId, serviceId, date: dayjs(date).format('YYYY-MM-DD') },
})

const openMovementEdit = (modelValue) => {
  actionMenu.value = null
  movementEdit.value = { modelValue }
}

const movementDateTime = (day) => dayjs(`${dayjs(day?.key).format('YYYY-MM-DD')}T${webSiteStore.getDefaultStartTime()}`).format('YYYY-MM-DDTHH:mm')

const newMovement = (item, day, overrides = {}) => openMovementEdit({
  service: item.id,
  warehouse: warehouseId.value ?? webSiteStore.getDefaultWarehouse(),
  scheduled_at: movementDateTime(day),
  quantity: 1,
  ...overrides,
})

const onMarkerClickAsync = async (marker) => {
  const movement = marker.sample
  if (movement.planned) {
    openInTimeline(movement.project_id, movement.service_id, movement.scheduled_at)
    return
  }
  if (!canEdit.value) return
  const { data: full } = await planningStore.fetchWarehouseMovementByIdAsync(movement.id)
  openMovementEdit(full)
}

const shortageQuantity = (item, day) => {
  const deficit = day.projected < 0 ? -day.projected : (item.minStock ?? 0) - day.projected
  return Math.max(1, deficit)
}

const targetWarehouseId = (day) => warehouseId.value ??
  day.byWarehouse.find(w => day.belowMinWarehouses.includes(w.name))?.id ??
  webSiteStore.getDefaultWarehouse()

const transferSource = (day, targetId) => [...day.byWarehouse]
  .filter(w => w.id !== targetId && w.physical > 0)
  .sort((a, b) => b.physical - a.physical)[0] ?? null

const openActions = (event, track, item) => {
  const day = dayAtEvent(event, track, item)
  if (day.status === OK) return
  const canvas = scrollRef.value.firstElementChild.getBoundingClientRect()
  const targetId = targetWarehouseId(day)
  const projects = [...new Map(dayCommitments(item, day).filter(bar => bar.project).map(bar => [bar.project.id, bar.project])).values()]
  actionMenu.value = {
    item,
    day,
    targetId,
    source: transferSource(day, targetId),
    projects,
    left: event.clientX - canvas.left,
    top: event.clientY - canvas.top,
  }
}

const onTransfer = ({ item, day, targetId, source }) => newMovement(item, day, {
  movement_type: TRANSFER,
  warehouse: source.id,
  destination_warehouse: targetId,
  destination_scheduled_at: movementDateTime(day),
  quantity: Math.min(source.physical, shortageQuantity(item, day)),
})

const onCreateOrder = ({ item, day, targetId }) => newMovement(item, day, {
  movement_type: TRANSFER,
  warehouse: webSiteStore.getDefaultOrdersWarehouse(),
  destination_warehouse: targetId,
  destination_scheduled_at: movementDateTime(day),
  quantity: shortageQuantity(item, day),
})

const onMovementSaved = async () => {
  movementEdit.value = null
  await data.reloadAsync()
}

const goToEntryAsync = async ({ item, episode }) => {
  scrollToDate(episode.start, CENTER_VIEWPORT_RATIO)
  await highlightRowAsync(item.id, containerRef)
}

// { serviceId, date } from the timeline context menu: the resource and day to bring into view.
const focus = route.query.serviceId
  ? { serviceId: Number(route.query.serviceId), date: dayjs(route.query.date ?? undefined) }
  : null

const focusServiceAsync = async ({ serviceId, date }) => {
  const item = items.value.find(i => i.id === serviceId)
  if (!item) return
  resourceFilter.value = resourceFilterEnum.ALL
  onlyCritical.value = false
  expanded.value = new Set([...expanded.value, serviceId])
  scrollToDate(date, CENTER_VIEWPORT_RATIO)
  await highlightRowAsync(serviceId, containerRef)
}

onMounted(async () => {
  initRange(focus?.date ?? dayjs(), INITIAL_SPAN_DAYS)
  await loadWindowAsync()
  await nextTick()
  if (focus) await focusServiceAsync(focus)
  else scrollToToday()
})
</script>

<template>
  <div ref="containerRef" class="availability-timeline">
    <div class="availability-timeline__toolbar">
      <q-btn-toggle
        :model-value="activePreset"
        :options="presetOptions"
        class="availability-timeline__segmented"
        no-caps
        unelevated
        dense
        toggle-color="primary"
        text-color="grey-8"
        @update:model-value="value => setZoomAsync(value)"
      />
      <div class="availability-timeline__zoom">
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="mdi-minus"
          :disable="!canZoomOut"
          @click="setZoomAsync(zoomIndex - 1)"
        >
          <q-tooltip>{{ $t('common.zoomOut') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="mdi-plus"
          :disable="!canZoomIn"
          @click="setZoomAsync(zoomIndex + 1)"
        >
          <q-tooltip>{{ $t('common.zoomIn') }}</q-tooltip>
        </q-btn>
      </div>
      <q-btn
        outline
        rounded
        dense
        no-caps
        color="primary"
        icon="mdi-calendar-today"
        :label="$t('common.today')"
        class="availability-timeline__today-btn"
        @click="scrollToToday"
      />
      <q-btn-toggle
        v-model="resourceFilter"
        :options="resourceOptions"
        class="availability-timeline__segmented"
        no-caps
        unelevated
        dense
        toggle-color="primary"
        text-color="grey-8"
      />
      <q-select
        v-model="warehouseId"
        :options="warehouseOptions"
        emit-value
        map-options
        dense
        outlined
        options-dense
        class="availability-timeline__warehouse"
      >
        <template #prepend>
          <q-icon name="mdi-warehouse" size="xs" />
        </template>
      </q-select>
      <q-toggle
        v-model="onlyCritical"
        dense
        :label="$t('warehouseAvailability.onlyCritical')"
        :color="warningColor"
      />

      <q-space />

      <q-btn
        unelevated
        rounded
        dense
        no-caps
        :color="criticalEntries.length ? warningColor : 'grey-5'"
        icon="mdi-alert"
        :label="criticalLabel"
        class="availability-timeline__shortages-btn"
      >
        <q-menu
          anchor="bottom right"
          self="top right"
          :offset="[0, 8]"
        >
          <div class="availability-timeline__menu-header">
            <q-avatar
              icon="mdi-alert"
              :color="warningColor"
              text-color="white"
              size="md"
            />
            <div class="col">
              <div class="text-subtitle1 text-weight-medium">{{ $t('warehouseAvailability.shortages') }}</div>
              <div class="text-caption text-grey-7">{{ $t('warehouseAvailability.shortagesHint') }}</div>
            </div>
          </div>
          <q-list v-if="criticalEntries.length" class="availability-timeline__menu-list">
            <q-item
              v-for="entry in criticalEntries"
              :key="entry.key"
              v-close-popup
              clickable
              class="q-pa-sm"
              @click="goToEntryAsync(entry)"
            >
              <list-item
                dense
                :avatar-icon="SERVICE_TYPE_ICONS[entry.item.type]"
                :label="itemTitle(entry.item)"
              >
                <template #caption>
                  {{ formatPeriod(entry.episode) }}
                  <template v-if="entry.episode.status === SHORTAGE">
                    · {{ $t('warehouseAvailability.missing', { count: -entry.episode.worst }) }}
                  </template>
                </template>
                <template #right>
                  <q-chip
                    dense
                    :outline="entry.episode.status !== SHORTAGE || !entry.episode.real"
                    :color="warningColor"
                    :text-color="entry.episode.status === SHORTAGE && entry.episode.real ? 'white' : warningColor"
                    :label="episodeLabel(entry.episode)"
                  />
                </template>
              </list-item>
            </q-item>
          </q-list>
          <div v-else class="q-pa-md text-grey-7">{{ $t('warehouseAvailability.noCritical') }}</div>
        </q-menu>
      </q-btn>

      <div class="availability-timeline__legend gt-md">
        <span class="availability-timeline__legend-item">
          <span class="availability-timeline__swatch availability-timeline__swatch--physical" />{{ $t('warehouseAvailability.physical') }}
        </span>
        <span class="availability-timeline__legend-item">
          <span class="availability-timeline__swatch availability-timeline__swatch--projected" />{{ $t('warehouseAvailability.projected') }}
        </span>
        <span class="availability-timeline__legend-item">
          <span class="availability-timeline__swatch availability-timeline__swatch--shortage" />{{ $t('warehouseAvailability.shortages') }}
        </span>
      </div>
    </div>

    <q-linear-progress
      v-if="data.state.loading"
      indeterminate
      color="primary"
    />
    <div
      ref="scrollRef"
      class="availability-timeline__scroll"
      @scroll="onScroll"
      @wheel="onWheel"
    >
      <div class="availability-timeline__canvas" :style="{ width: `${taskListWidth + trackWidth}rem` }">
        <div class="availability-timeline__header">
          <div class="availability-timeline__corner" :style="{ width: `${taskListWidth}rem` }">
            <span class="availability-timeline__corner-title">{{ $t('warehouseAvailability.resources') }}</span>
            <q-badge rounded color="primary" :label="visibleItems.length" />
            <search-input v-model="resourceSearch" class="availability-timeline__search" />
            <div class="availability-timeline__resizer" @pointerdown="onResizerPointerDown" />
          </div>
          <div class="availability-timeline__scale" :style="{ width: `${trackWidth}rem` }" @pointerdown="onPanStart">
            <project-timeline-scale
              :range-start="rangeStart"
              :range-days="rangeDays"
              :day-width="dayWidth"
              :sticky-offset="taskListWidth"
            />
          </div>
        </div>

        <div class="availability-timeline__body">
          <div class="availability-timeline__grid" :style="gridStyle" />
          <div class="availability-timeline__today" :style="{ left: `${todayLeft}rem` }" />

          <template v-for="item in visibleItems" :key="item.id">
            <div :class="['availability-timeline__row', { 'availability-timeline__row--critical': isCritical(item) }]">
              <div
                :class="['availability-timeline__cell', { 'row-highlight': highlightedRowId === item.id }]"
                :style="{ width: `${taskListWidth}rem` }"
              >
                <div class="availability-timeline__accent" />
                <q-btn
                  flat
                  round
                  dense
                  size="sm"
                  color="grey-7"
                  :icon="expanded.has(item.id) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                  @click="toggleExpanded(item)"
                />
                <q-avatar size="1.75rem" color="grey-3" text-color="grey-8">
                  <img v-if="item.avatar_file || item.avatar_url" :src="item.avatar_file || item.avatar_url">
                  <q-icon v-else :name="SERVICE_TYPE_ICONS[item.type]" size="1rem" />
                </q-avatar>
                <div class="availability-timeline__titles">
                  <div class="availability-timeline__title">{{ itemTitle(item) }}</div>
                  <div class="availability-timeline__subtitle">
                    {{ itemSubtitle(item) }}
                    <template v-if="supportsMinStock(item) && (canEdit || item.minStock !== null)">
                      ·
                      <span
                        :class="['availability-timeline__min-link', { 'availability-timeline__min-link--empty': item.minStock === null }]"
                        @click.stop="minStockItemId = canEdit ? item.id : null"
                      >
                        <q-icon v-if="canEdit" name="mdi-pencil" size="0.6875rem" />
                        {{ minStockLabel(item) }}
                        <q-menu
                          v-if="canEdit"
                          :model-value="minStockItemId === item.id"
                          no-parent-event
                          anchor="bottom left"
                          self="top left"
                          :offset="[0, 4]"
                          @update:model-value="value => { if (!value) minStockItemId = null }"
                        >
                          <div class="availability-timeline__min-editor">
                            <div class="row items-center no-wrap">
                              <div class="col text-subtitle2">{{ $t('warehouseAvailability.minStockByWarehouse') }}</div>
                              <q-btn
                                flat
                                round
                                dense
                                size="sm"
                                icon="mdi-close"
                                @click="minStockItemId = null"
                              />
                            </div>
                            <div class="text-caption text-grey-7 q-mb-sm">{{ $t('warehouseAvailability.minStockHint') }}</div>
                            <q-input
                              v-for="warehouse in data.state.warehouses"
                              :key="warehouse.id"
                              :model-value="item.minByWarehouse[warehouse.id] ?? null"
                              :label="warehouse.name"
                              :hint="`${$t('warehouseAvailability.physical')}: ${stockToday(item, warehouse.id)}`"
                              :placeholder="$t('warehouseAvailability.none')"
                              :debounce="webSiteStore.getInputDebounce()"
                              type="number"
                              min="0"
                              dense
                              outlined
                              clearable
                              stack-label
                              class="q-mb-sm"
                              @update:model-value="value => onMinStockChange(item, warehouse.id, value)"
                            />
                          </div>
                        </q-menu>
                      </span>
                    </template>
                  </div>
                </div>
                <q-icon
                  v-if="item.shortages.length"
                  name="mdi-alert"
                  size="1.125rem"
                  :color="warningColor"
                >
                  <q-tooltip>{{ episodeLabel(item.shortages[0]) }} · {{ formatPeriod(item.shortages[0]) }}</q-tooltip>
                </q-icon>
                <q-icon
                  v-else-if="item.belowMin.length"
                  name="mdi-alert-outline"
                  size="1.125rem"
                  :color="warningColor"
                >
                  <q-tooltip>{{ $t('warehouseAvailability.belowMin') }} · {{ formatPeriod(item.belowMin[0]) }}</q-tooltip>
                </q-icon>
              </div>

              <div
                class="availability-timeline__track"
                :style="{ width: `${trackWidth}rem` }"
                @pointerdown="onPanStart($event, (e, track) => openActions(e, track, item))"
                @pointermove="onTrackMove($event, item)"
                @pointerleave="hover = null"
              >
                <div class="availability-timeline__series" :style="seriesStyle">
                  <div
                    v-for="episode in item.belowMin"
                    :key="`min-${episode.startIndex}`"
                    class="availability-timeline__band availability-timeline__band--min"
                    :style="bandStyle(episode)"
                  />
                  <div
                    v-for="episode in item.shortages"
                    :key="`short-${episode.startIndex}`"
                    :class="['availability-timeline__band', episode.real ? 'availability-timeline__band--real' : 'availability-timeline__band--potential']"
                    :style="bandStyle(episode)"
                  >
                    <span
                      v-if="(episode.endIndex - episode.startIndex + 1) * dayWidth >= LABEL_MIN_WIDTH || dayWidth >= DAY_LINES_MIN_WIDTH"
                      class="availability-timeline__badge"
                    >{{ episodeBadge(episode) }}</span>
                  </div>
                  <svg
                    class="availability-timeline__chart"
                    :viewBox="`0 0 ${item.days.length} 100`"
                    preserveAspectRatio="none"
                  >
                    <line
                      x1="0"
                      :x2="item.days.length"
                      :y1="CHART_ZERO"
                      :y2="CHART_ZERO"
                      class="availability-timeline__zero"
                    />
                    <line
                      v-if="item.minStock !== null"
                      x1="0"
                      :x2="item.days.length"
                      :y1="chartY(item, item.minStock)"
                      :y2="chartY(item, item.minStock)"
                      class="availability-timeline__min-line"
                    />
                    <path :d="areaPath(item)" class="availability-timeline__area" />
                    <rect
                      v-for="rect in deficitRects(item)"
                      :key="rect.x"
                      :x="rect.x"
                      :y="rect.y"
                      width="1"
                      :height="rect.height"
                      :class="['availability-timeline__deficit', { 'availability-timeline__deficit--real': rect.real }]"
                    />
                    <path :d="projectedPath(item)" class="availability-timeline__projected" />
                  </svg>
                </div>
              </div>
            </div>

            <template v-if="expanded.has(item.id)">
              <div class="availability-timeline__row availability-timeline__row--lane">
                <div class="availability-timeline__cell availability-timeline__cell--lane" :style="{ width: `${taskListWidth}rem` }">
                  <q-icon name="mdi-calendar-clock" size="0.875rem" />
                  <span>{{ $t('warehouseAvailability.commitments') }}</span>
                  <q-badge rounded color="grey-5" :label="item.commitments.length" />
                </div>
                <div
                  class="availability-timeline__track availability-timeline__track--lane"
                  :style="{ width: `${trackWidth}rem`, height: laneHeight(item) }"
                  @pointerdown="onPanStart"
                >
                  <div class="availability-timeline__series" :style="seriesStyle">
                    <div
                      v-for="bar in commitmentBars(item)"
                      :key="bar.key"
                      :class="['availability-timeline__bar', { 'availability-timeline__bar--unconfirmed': !bar.confirmed }]"
                      :style="{
                        left: `${dayLeft(bar.startIndex)}rem`,
                        width: `${(bar.endIndex - bar.startIndex + 1) * dayWidth}rem`,
                        top: `${bar.lane * LANE_HEIGHT + 0.25}rem`,
                        '--bar-color': barColor(bar),
                      }"
                      @pointerdown.stop
                      @click="bar.project && openInTimeline(bar.project.id, item.id, bar.start)"
                    >
                      <q-icon v-if="bar.type === ABSENCE" :name="ABSENCE_ICON" size="0.75rem" />
                      <span class="ellipsis">{{ barLabel(bar) }}</span>
                      <q-tooltip>{{ barTooltip(bar) }}</q-tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div class="availability-timeline__row availability-timeline__row--lane">
                <div class="availability-timeline__cell availability-timeline__cell--lane" :style="{ width: `${taskListWidth}rem` }">
                  <q-icon name="mdi-swap-vertical" size="0.875rem" />
                  <span>{{ $t('warehouseAvailability.movements') }}</span>
                  <q-badge rounded color="grey-5" :label="item.movements.length" />
                  <q-space />
                  <q-btn
                    v-if="canEdit && !item.isStaff"
                    flat
                    round
                    dense
                    size="sm"
                    icon="mdi-plus-circle-outline"
                    @click="newMovement(item, null)"
                  >
                    <q-tooltip>{{ $t('common.addMovement') }}</q-tooltip>
                  </q-btn>
                </div>
                <div class="availability-timeline__track availability-timeline__track--movements" :style="{ width: `${trackWidth}rem` }" @pointerdown="onPanStart">
                  <div class="availability-timeline__series" :style="seriesStyle">
                    <div
                      v-for="marker in movementMarkers(item)"
                      :key="marker.key"
                      :class="['availability-timeline__marker', {
                        'availability-timeline__marker--in': isIncoming(marker.sample),
                        'availability-timeline__marker--order': marker.sample.from_virtual,
                        'availability-timeline__marker--planned': marker.sample.planned,
                      }]"
                      :style="markerStyle(marker)"
                      @pointerdown.stop
                      @click="onMarkerClickAsync(marker)"
                    >
                      <q-icon :name="movementIcon(marker.sample)" size="0.75rem" />
                      <span>{{ marker.quantity }}</span>
                      <q-tooltip>
                        <div class="text-weight-bold">{{ formatDay(marker.sample.scheduled_at) }}</div>
                        <div v-for="movement in marker.movements" :key="movement.id">{{ movementLine(movement) }}</div>
                      </q-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <div v-if="!visibleItems.length && !data.state.loading" class="availability-timeline__empty">
            {{ onlyCritical ? $t('warehouseAvailability.noCritical') : $t('common.noData') }}
          </div>

          <div
            v-if="actionMenu"
            class="availability-timeline__menu-anchor"
            :style="{ left: `${actionMenu.left}px`, top: `${actionMenu.top}px` }"
          >
            <q-menu
              :model-value="true"
              anchor="bottom left"
              self="top left"
              @hide="actionMenu = null"
            >
              <q-list dense class="availability-timeline__action-list">
                <q-item-label header>
                  {{ itemTitle(actionMenu.item) }} · {{ formatDay(actionMenu.day.key) }}
                </q-item-label>
                <template v-if="canEdit && !actionMenu.item.isStaff">
                  <q-item
                    v-if="actionMenu.source"
                    v-close-popup
                    clickable
                    @click="onTransfer(actionMenu)"
                  >
                    <q-item-section avatar><q-icon name="mdi-truck-fast-outline" /></q-item-section>
                    <q-item-section>
                      {{ $t('warehouseAvailability.transferFrom') }}
                      <q-item-label caption>{{ actionMenu.source.name }}: {{ actionMenu.source.physical }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item v-close-popup clickable @click="onCreateOrder(actionMenu)">
                    <q-item-section avatar><q-icon name="mdi-truck-delivery-outline" /></q-item-section>
                    <q-item-section>{{ $t('warehouseAvailability.createOrder') }}</q-item-section>
                  </q-item>
                  <q-item v-close-popup clickable @click="newMovement(actionMenu.item, actionMenu.day)">
                    <q-item-section avatar><q-icon name="mdi-plus-circle-outline" /></q-item-section>
                    <q-item-section>{{ $t('common.addMovement') }}</q-item-section>
                  </q-item>
                </template>
                <q-item
                  v-for="project in actionMenu.projects"
                  :key="project.id"
                  v-close-popup
                  clickable
                  @click="openInTimeline(project.id, actionMenu.item.id, actionMenu.day.key)"
                >
                  <q-item-section avatar>
                    <span class="availability-timeline__tooltip-dot" :style="{ backgroundColor: project.color }" />
                  </q-item-section>
                  <q-item-section>{{ $t('warehouseAvailability.openProject', { project: project.name }) }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hover"
      class="availability-timeline__tooltip"
      :style="{ left: `${hover.x}px`, top: `${hover.y}px` }"
    >
      <div class="text-weight-bold">{{ formatDay(hover.day.key) }}</div>
      <div v-if="hoverStatusLabel" class="availability-timeline__tooltip-status">
        <q-icon name="mdi-alert" size="0.875rem" />{{ hoverStatusLabel }}
      </div>
      <div class="availability-timeline__tooltip-row">
        <span>{{ $t('warehouseAvailability.physical') }}</span><b>{{ hover.day.physical }}</b>
      </div>
      <div class="availability-timeline__tooltip-row">
        <span>{{ $t('warehouseAvailability.planned') }}</span><b>{{ hover.day.planned }}</b>
      </div>
      <div class="availability-timeline__tooltip-row">
        <span>{{ $t('warehouseAvailability.projected') }}</span><b>{{ hover.day.projected }}</b>
      </div>
      <div v-if="hover.item.minStock !== null" class="availability-timeline__tooltip-row">
        <span>{{ $t('warehouseAvailability.minStock') }}</span><b>{{ hover.item.minStock }}</b>
      </div>
      <div v-if="hover.day.belowMinWarehouses.length" class="availability-timeline__tooltip-status">
        {{ $t('warehouseAvailability.belowMinIn', { warehouses: hover.day.belowMinWarehouses.join(', ') }) }}
      </div>
      <div v-if="hover.day.elsewhere > 0" class="availability-timeline__tooltip-row">
        <span>{{ $t('warehouseAvailability.elsewhere') }}</span><b>{{ hover.day.elsewhere }}</b>
      </div>
      <template v-if="dayCommitments(hover.item, hover.day).length">
        <div class="availability-timeline__tooltip-section">{{ $t('warehouseAvailability.dayCommitments') }}</div>
        <div v-for="bar in dayCommitments(hover.item, hover.day)" :key="bar.key ?? bar.id" class="availability-timeline__tooltip-line">
          <span class="availability-timeline__tooltip-dot" :style="{ backgroundColor: barColor(bar) }" />
          {{ commitmentLine(bar) }}
        </div>
      </template>
      <template v-if="dayMovements(hover.item, hover.day).length">
        <div class="availability-timeline__tooltip-section">{{ $t('warehouseAvailability.dayMovements') }}</div>
        <div v-for="movement in dayMovements(hover.item, hover.day)" :key="movement.id" class="availability-timeline__tooltip-line">
          <q-icon :name="movementIcon(movement)" size="0.75rem" />
          {{ movementLine(movement) }}
        </div>
      </template>
    </div>

    <q-dialog
      :maximized="isMobile"
      :model-value="movementEdit !== null"
      class="dialog-xs"
      @hide="movementEdit = null"
    >
      <warehouse-movement-edit
        v-if="movementEdit"
        :model-value="movementEdit.modelValue"
        @update="onMovementSaved"
        @delete="onMovementSaved"
        @cancel="movementEdit = null"
      />
    </q-dialog>
  </div>
</template>

<style scoped lang="scss">
.availability-timeline {
  --timeline-surface: #{$white};
  --timeline-surface-alt: color-mix(in srgb, var(--q-primary) 3%, var(--timeline-surface));
  --timeline-border: color-mix(in srgb, var(--q-primary) 15%, transparent);
  --timeline-line: rgba(0, 0, 0, 0.06);
  --timeline-line-strong: rgba(0, 0, 0, 0.1);
  --timeline-weekend: color-mix(in srgb, var(--q-primary) 4%, transparent);
  --timeline-text: #1f1d2b;
  --timeline-text-muted: #8b879a;
  --timeline-today: var(--q-secondary);
  --timeline-tooltip-bg: #1f1d2b;
  --timeline-warning: var(--q-warning);
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 7rem);
  min-height: 0;
  overflow: hidden;
  background: var(--timeline-surface);
  border: 1px solid var(--timeline-border);
  border-radius: $app-border-radius;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  color: var(--timeline-text);
}

:global(.body--dark) .availability-timeline {
  --timeline-surface: #{$dark};
  --timeline-border: color-mix(in srgb, var(--q-primary) 30%, transparent);
  --timeline-line: rgba(255, 255, 255, 0.07);
  --timeline-line-strong: rgba(255, 255, 255, 0.12);
  --timeline-weekend: color-mix(in srgb, var(--q-primary) 8%, transparent);
  --timeline-text: #ecebf2;
  --timeline-text-muted: #9a96aa;
  --timeline-tooltip-bg: #3a3748;
}

.availability-timeline__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--timeline-surface-alt);
  border-bottom: 1px solid var(--timeline-border);
}

.availability-timeline__segmented {
  border: 1px solid var(--timeline-border);
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--timeline-surface);

  :deep(.q-btn) {
    padding: 0.125rem 0.75rem;
    font-size: 0.8125rem;
  }
}

.availability-timeline__zoom {
  display: flex;
  border: 1px solid var(--timeline-border);
  border-radius: 0.5rem;
  background: var(--timeline-surface);
}

.availability-timeline__today-btn,
.availability-timeline__shortages-btn {
  padding: 0 0.75rem;
  font-size: 0.8125rem;
}

.availability-timeline__warehouse {
  min-width: 12rem;
  font-size: 0.8125rem;
}

.availability-timeline__legend {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: var(--timeline-text-muted);
}

.availability-timeline__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.availability-timeline__swatch {
  width: 1.25rem;
  height: 0.625rem;
  border-radius: 0.25rem;

  &--physical {
    background: color-mix(in srgb, var(--q-primary) 25%, transparent);
    border-top: 2px solid var(--q-primary);
  }

  &--projected {
    border-top: 2px dashed var(--timeline-text);
  }

  &--shortage {
    background: var(--timeline-warning);
  }
}

.availability-timeline__menu-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  max-width: 28rem;
}

.availability-timeline__menu-list {
  min-width: 26rem;
  max-height: 60vh;
  overflow-y: auto;
}

.availability-timeline__scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--q-primary) 30%, transparent) transparent;
}

.gantt--panning,
.gantt--panning :deep(*) {
  cursor: grabbing !important;
}

.availability-timeline__canvas {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.availability-timeline__header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-shrink: 0;
  height: 3.5rem;
  background: var(--timeline-surface);
  border-bottom: 1px solid var(--timeline-border);
}

.availability-timeline__corner {
  position: sticky;
  left: 0;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  background: var(--timeline-surface);
  border-right: 1px solid var(--timeline-border);
}

.availability-timeline__search {
  flex: 0 1 14rem;
  margin-left: auto;
  background: var(--timeline-surface);
}

.availability-timeline__corner-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.availability-timeline__resizer {
  position: absolute;
  top: 0;
  right: -0.25rem;
  bottom: 0;
  width: 0.5rem;
  cursor: col-resize;
  z-index: 2;

  &:hover {
    background: linear-gradient(to right, transparent 0.1875rem, var(--q-primary) 0.1875rem 0.3125rem, transparent 0.3125rem);
  }
}

.availability-timeline__scale {
  flex-shrink: 0;
  cursor: grab;
}

.availability-timeline__body {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.availability-timeline__grid {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
}

.availability-timeline__today {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px solid var(--timeline-today);
  z-index: 2;
  pointer-events: none;
}

.availability-timeline__row {
  display: flex;
  min-height: 3rem;
  border-bottom: 1px solid var(--timeline-line);
  transition: background-color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 4%, transparent);
  }

  &--critical .availability-timeline__accent {
    background: var(--timeline-warning);
  }

  &--lane {
    min-height: 2rem;
    background: color-mix(in srgb, var(--q-primary) 2%, transparent);
  }
}

.availability-timeline__cell {
  position: sticky;
  left: 0;
  z-index: 3;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem 0 0.25rem;
  background: var(--timeline-surface);
  border-right: 1px solid var(--timeline-border);
  overflow: hidden;

  &--lane {
    padding-left: 3.5rem;
    font-size: 0.75rem;
    color: var(--timeline-text-muted);
  }
}

.availability-timeline__accent {
  align-self: stretch;
  width: 0.1875rem;
  margin: 0.375rem 0;
  border-radius: 0.125rem;
  background: transparent;
}

.availability-timeline__titles {
  flex: 1;
  min-width: 0;
}

.availability-timeline__title {
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.availability-timeline__subtitle {
  font-size: 0.6875rem;
  color: var(--timeline-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.availability-timeline__min-link {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  color: var(--q-primary);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &--empty {
    color: var(--timeline-text-muted);
  }
}

.availability-timeline__min-editor {
  width: 18rem;
  padding: 0.75rem 1rem 0.25rem;
}

.availability-timeline__series {
  position: absolute;
  top: 0;
  bottom: 0;
}

.availability-timeline__track {
  position: relative;
  flex-shrink: 0;
  cursor: grab;

  &--lane {
    min-height: 2rem;
  }
}

.availability-timeline__chart {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.availability-timeline__area {
  fill: color-mix(in srgb, var(--q-primary) 18%, transparent);
  stroke: var(--q-primary);
  stroke-width: 1.5;
  vector-effect: non-scaling-stroke;
}

.availability-timeline__projected {
  fill: none;
  stroke: var(--timeline-text);
  stroke-width: 1.25;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
}

.availability-timeline__zero {
  stroke: var(--timeline-line-strong);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.availability-timeline__min-line {
  stroke: var(--timeline-warning);
  stroke-width: 1;
  stroke-dasharray: 2 3;
  vector-effect: non-scaling-stroke;
}

.availability-timeline__deficit {
  fill: color-mix(in srgb, var(--timeline-warning) 55%, transparent);

  &--real {
    fill: var(--timeline-warning);
  }
}

.availability-timeline__band {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;

  &--real {
    background: color-mix(in srgb, var(--timeline-warning) 22%, transparent);
    border-left: 2px solid var(--timeline-warning);
  }

  &--potential {
    background: repeating-linear-gradient(
      135deg,
      color-mix(in srgb, var(--timeline-warning) 22%, transparent) 0 0.25rem,
      color-mix(in srgb, var(--timeline-warning) 8%, transparent) 0.25rem 0.5rem
    );
    border-left: 2px dashed var(--timeline-warning);
  }

  &--min {
    background: color-mix(in srgb, var(--timeline-warning) 8%, transparent);
  }
}

.availability-timeline__badge {
  position: absolute;
  top: 0.125rem;
  left: 0.25rem;
  padding: 0 0.3125rem;
  border-radius: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1rem;
  color: $white;
  background: var(--timeline-warning);
}

.availability-timeline__bar {
  --bar-color: var(--q-primary);
  position: absolute;
  height: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0 0.375rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  color: $white;
  background: var(--bar-color);
  overflow: hidden;
  cursor: pointer;

  &--unconfirmed {
    color: var(--timeline-text);
    background: repeating-linear-gradient(
      135deg,
      color-mix(in srgb, var(--bar-color) 30%, var(--timeline-surface)) 0 0.1875rem,
      color-mix(in srgb, var(--bar-color) 12%, var(--timeline-surface)) 0.1875rem 0.375rem
    );
    border: 1px dashed color-mix(in srgb, var(--bar-color) 60%, transparent);
  }

}

.availability-timeline__track--movements {
  min-height: 3rem;
}

.availability-timeline__marker {
  --marker-color: var(--timeline-text-muted);
  position: absolute;
  bottom: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  height: 1.125rem;
  padding: 0 0.3125rem 0 0.1875rem;
  border-radius: 0.5625rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: $white;
  background: var(--marker-color);
  transform: translateX(calc(-50% + var(--marker-slot) * 105%));
  white-space: nowrap;
  cursor: pointer;

  &--in {
    --marker-color: var(--q-primary);
    top: 0.25rem;
    bottom: auto;
  }

  &--order {
    --marker-color: var(--q-positive);
  }

  &--planned {
    color: var(--marker-color);
    background: var(--timeline-surface);
    border: 1px dashed var(--marker-color);
  }

}

.availability-timeline__menu-anchor {
  position: absolute;
  width: 1px;
  height: 1px;
}

.availability-timeline__action-list {
  min-width: 18rem;
}

.availability-timeline__empty {
  padding: 2rem;
  text-align: center;
  color: var(--timeline-text-muted);
}

.availability-timeline__tooltip {
  position: fixed;
  z-index: 9000;
  transform: translate(0.75rem, 0.75rem);
  min-width: 13rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  color: $white;
  background: var(--timeline-tooltip-bg);
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.availability-timeline__tooltip-status {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0.125rem 0 0.25rem;
  color: var(--timeline-warning);
  font-weight: 600;
}

.availability-timeline__tooltip-section {
  margin: 0.375rem 0 0.125rem;
  padding-top: 0.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 600;
  opacity: 0.8;
}

.availability-timeline__tooltip-line {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.availability-timeline__tooltip-dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
}

.availability-timeline__tooltip-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
</style>
